// Google Analytics for Firebase: pantallas vistas y acciones clave de cada rol, para el panel de Analytics en
// la consola de Firebase. La app de Android es una TWA que abre esta misma PWA, así que queda medida también.
//
// Nunca se mandan datos personales (nombres, documentos, celulares, apartamentos, PIN): solo el nombre de la
// acción, el rol y el id interno del conjunto. El SDK se carga aparte para no frenar la primera pantalla, y si
// el navegador no lo soporta (o bloquea el rastreo) la app sigue igual: medir nunca puede romper nada.

import type { Analytics } from "firebase/analytics";
import { app } from "./firebase";
import { usaEmulador } from "./config";

type Parametros = Record<string, string | number | boolean | undefined>;
type Modulo = typeof import("firebase/analytics");

const activa = !usaEmulador && import.meta.env.PROD;
// send_page_view: false porque la primera pantalla también la manda registrarPantalla(), con la ruta sin ids.

let cargando: Promise<{ a: Analytics; m: Modulo } | null> | null = null;

function cargar() {
  if (!activa) return Promise.resolve(null);
  cargando ??= import("firebase/analytics")
    .then(async (m) => ((await m.isSupported()) ? { a: m.initializeAnalytics(app, { config: { send_page_view: false } }), m } : null))
    .catch(() => null);
  return cargando;
}

function conAnalytics(f: (a: Analytics, m: Modulo) => void) {
  cargar()
    .then((r) => r && f(r.a, r.m))
    .catch(() => {});
}

/** Rol de la pantalla según la ruta (/c/:cid/residente…, /admin/:cid…, /plataforma…). */
export function rolDeRuta(ruta: string): string | undefined {
  const c = /^\/c\/[^/]+\/(residente|domiciliario|vigilante)(\/|$)/.exec(ruta);
  if (c) return c[1];
  if (/^\/admin\//.test(ruta)) return "admin";
  if (/^\/plataforma(\/|$)/.test(ruta)) return "plataforma";
  return undefined;
}

function conjuntoDeRuta(ruta: string): string | undefined {
  return /^\/(?:c|admin)\/([^/]+)/.exec(ruta)?.[1];
}

/** Ruta sin ids ni ?codigo=…: así todas las visitas a un mismo panel se agrupan en una sola pantalla. */
export function rutaSinIds(ruta: string): string {
  return ruta.replace(/^\/c\/[^/]+/, "/c/:conjunto").replace(/^\/admin\/[^/]+/, "/admin/:conjunto").replace(/\/+$/, "") || "/";
}

/** Título legible para el informe "Páginas y pantallas" (todas las rutas comparten el <title> de la app). */
function tituloDe(pantalla: string): string {
  const partes = pantalla.split("/").filter((p) => p && p !== ":conjunto" && p !== "c");
  return partes.length ? partes.join(" · ") : "inicio";
}

let rolActual: string | undefined;
let conjuntoActual: string | undefined;

/** Registra una pantalla vista (page_view). Se llama en cada cambio de ruta. */
export function registrarPantalla(ruta: string) {
  rolActual = rolDeRuta(ruta);
  conjuntoActual = conjuntoDeRuta(ruta);
  const pantalla = rutaSinIds(ruta);
  conAnalytics((a, m) => {
    if (rolActual) m.setUserProperties(a, { rol: rolActual });
    const comunes = { rol: rolActual, conjunto_id: conjuntoActual };
    m.logEvent(a, "page_view", { page_path: pantalla, page_location: location.origin + pantalla, page_title: tituloDe(pantalla), ...comunes });
  });
}

/** Evento propio. Agrega el rol y el conjunto de la pantalla actual. */
export function registrarEvento(nombre: string, parametros: Parametros = {}) {
  conAnalytics((a, m) => m.logEvent(a, nombre, { rol: rolActual, conjunto_id: conjuntoActual, ...parametros }));
}

/** Vincula los eventos a la cuenta (uid de Firebase, que no identifica a la persona) o la suelta al salir. */
export function registrarCuenta(uid: string | null) {
  conAnalytics((a, m) => m.setUserId(a, uid));
}

/**
 * Cloud Functions que cuentan como acción clave, con el evento que generan. Solo los parámetros de la lista
 * `campos` pasan a Analytics (nunca nombres, documentos ni notas).
 */
const ACCIONES: Record<string, { evento: string; rol?: string; campos?: string[] }> = {
  // Residente
  residenteCrearPedido: { evento: "pedido_creado", rol: "residente", campos: ["tamano"] },
  residenteCrearPedidoAnticipado: { evento: "pedido_anticipado_creado", rol: "residente", campos: ["tamano"] },
  residenteCancelarPedido: { evento: "pedido_cancelado", rol: "residente" },
  residenteReconocerPaquete: { evento: "paquete_reconocido", rol: "residente", campos: ["esMio"] },
  residenteDecidirFamiliar: { evento: "familiar_decidido", rol: "residente", campos: ["conoce"] },
  reportarIncidente: { evento: "incidente_reportado", campos: ["tipo"] },
  // Domiciliario
  domiciliarioCambiarDisponible: { evento: "domiciliario_disponibilidad", rol: "domiciliario", campos: ["disponible"] },
  domiciliarioAceptarPedido: { evento: "pedido_tomado", rol: "domiciliario" },
  domiciliarioSoltarPedido: { evento: "pedido_soltado", rol: "domiciliario" },
  domiciliarioConfirmarEntrega: { evento: "pedido_entregado", rol: "domiciliario" },
  domiciliarioMarcarCobrado: { evento: "pedido_cobrado", rol: "domiciliario" },
  // Vigilante (portería)
  vigilanteCambiarTurno: { evento: "vigilante_turno", rol: "vigilante", campos: ["enTurno"] },
  vigilanteRegistrarPaquete: { evento: "paquete_registrado", rol: "vigilante" },
  vigilanteVerificarCodigo: { evento: "codigo_verificado", rol: "vigilante" },
  vigilanteEntregar: { evento: "paquete_entregado_a_domiciliario", rol: "vigilante" },
  vigilanteMarcarRecogido: { evento: "paquete_recogido_en_porteria", rol: "vigilante" },
  vigilanteDevolverPaquete: { evento: "paquete_devuelto", rol: "vigilante" },
  vigilanteCorregirPaquete: { evento: "paquete_corregido", rol: "vigilante" },
  vigilanteRecibirDevolucion: { evento: "devolucion_recibida", rol: "vigilante" },
  // Administración
  adminDecidirMembresia: { evento: "admin_membresia_decidida", rol: "admin", campos: ["rol", "aprobar"] },
  adminCambiarEstadoCuenta: { evento: "admin_cuenta_cambiada", rol: "admin", campos: ["rol", "habilitar"] },
  adminCrearPersonal: { evento: "admin_personal_creado", rol: "admin", campos: ["rol"] },
  adminNuevaClave: { evento: "admin_clave_nueva", rol: "admin" },
  adminActualizarConjunto: { evento: "admin_configuracion_guardada", rol: "admin" },
  adminCambiarLogo: { evento: "admin_logo_cambiado", rol: "admin" },
  adminPreautorizar: { evento: "admin_preaprobados", rol: "admin" },
  adminEditarPreautorizacion: { evento: "admin_preaprobado_editado", rol: "admin" },
  adminEliminarPreautorizaciones: { evento: "admin_preaprobados_eliminados", rol: "admin" },
  adminCargarUnidades: { evento: "admin_apartamentos_cargados", rol: "admin" },
  adminEditarUnidad: { evento: "admin_apartamento_editado", rol: "admin" },
  adminEliminarUnidades: { evento: "admin_apartamentos_eliminados", rol: "admin" },
  adminRenombrarTorre: { evento: "admin_torre_renombrada", rol: "admin" },
  adminRecibirDevolucion: { evento: "admin_devolucion_recibida", rol: "admin" },
  adminResolverIncidente: { evento: "admin_incidente_resuelto", rol: "admin" },
  // Cuenta y conjuntos
  completarRegistro: { evento: "sign_up", campos: [] },
  buscarConjunto: { evento: "conjunto_buscado" },
  solicitarMembresia: { evento: "membresia_solicitada", campos: ["rol"] },
  actualizarFoto: { evento: "foto_actualizada" },
  // Plataforma
  plataformaCrearConjunto: { evento: "plataforma_conjunto_creado", rol: "plataforma" },
  plataformaActualizarConjunto: { evento: "plataforma_conjunto_actualizado", rol: "plataforma" },
  plataformaRegistrarCuadre: { evento: "plataforma_cuadre_registrado", rol: "plataforma" },
};

function camposPermitidos(datos: Record<string, unknown>, campos: string[] = []): Parametros {
  const fuera: Parametros = {};
  for (const c of campos) {
    const v = datos[c];
    // "rol" es el de la persona sobre la que se actúa: no debe pisar el rol de quien hace la acción.
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") fuera[c === "rol" ? "rol_persona" : c.toLowerCase()] = v;
  }
  return fuera;
}

/** Lo llama `llamar()` tras cada Cloud Function: registra la acción si es clave, o el error si falló. */
export function registrarAccion(funcion: string, datos: Record<string, unknown>, error?: { code?: string }) {
  const accion = ACCIONES[funcion];
  if (!accion) return;
  const conjunto = typeof datos.conjuntoId === "string" ? datos.conjuntoId : undefined;
  const base: Parametros = { ...(conjunto ? { conjunto_id: conjunto } : {}), ...(accion.rol ? { rol: accion.rol } : {}) };
  if (error) {
    registrarEvento("accion_fallida", { ...base, accion: accion.evento, codigo: (error.code ?? "desconocido").replace(/^functions\//, "") });
    return;
  }
  const extra: Parametros = { ...base, ...camposPermitidos(datos, accion.campos) };
  if (funcion === "vigilanteEntregar" && Array.isArray(datos.pedidoIds)) extra.pedidos = datos.pedidoIds.length;
  if (funcion === "adminPreautorizar" && Array.isArray(datos.filas)) extra.filas = datos.filas.length;
  if (funcion === "adminEliminarUnidades" && Array.isArray(datos.unidadIds)) extra.apartamentos = datos.unidadIds.length;
  if (funcion === "completarRegistro") extra.method = "documento";
  registrarEvento(accion.evento, extra);
}
